import { FC, useState, useEffect } from 'react'
import * as XLSX from 'sheetjs-style'
import jsPDF from 'jspdf'
import { useNoSaleReasons } from '../hooks/useNoSaleReasons'

interface NoSaleReasonsTabProps {
  pluginspaceId: number
}

const getFileTimestamp = () => {
  const now = new Date()
  const year = now.getFullYear()
  const month = String(now.getMonth() + 1).padStart(2, '0')
  const day = String(now.getDate()).padStart(2, '0')
  const hours = String(now.getHours()).padStart(2, '0')
  const minutes = String(now.getMinutes()).padStart(2, '0')
  const seconds = String(now.getSeconds()).padStart(2, '0')
  return `${year}${month}${day}-${hours}${minutes}${seconds}`
}

export const NoSaleReasonsTab: FC<NoSaleReasonsTabProps> = ({ pluginspaceId }) => {
  const { reasons, loading, loadReasonStatistics } = useNoSaleReasons(pluginspaceId)
  const [days, setDays] = useState(7)
  const [startDate, setStartDate] = useState('')
  const [endDate, setEndDate] = useState('')
  const [periodLabel, setPeriodLabel] = useState('Últimos 7 dias')

  const formatDateLabel = (dateValue: string) => {
    const [year, month, day] = dateValue.split('-')
    if (!year || !month || !day) return dateValue
    return `${day}/${month}/${year}`
  }

  useEffect(() => {
    loadReasonStatistics({ days })
  }, [days, loadReasonStatistics])

  const handleFilterDays = async (newDays: number) => {
    setDays(newDays)
    setStartDate('')
    setEndDate('')
    setPeriodLabel(`Últimos ${newDays} dias`)
    await loadReasonStatistics({ days: newDays })
  }

  const handleFilterByDateRange = async () => {
    if (!startDate || !endDate) {
      return
    }

    setPeriodLabel(`${formatDateLabel(startDate)} até ${formatDateLabel(endDate)}`)
    await loadReasonStatistics({
      days: undefined,
      startDate: startDate || undefined,
      endDate: endDate || undefined,
    })
  }

  const handleClearDateRange = async () => {
    setStartDate('')
    setEndDate('')
    setDays(7)
    setPeriodLabel('Últimos 7 dias')
    await loadReasonStatistics({ days: 7 })
  }

  const getPeriodText = () => {
    if (startDate && endDate) {
      return `${formatDateLabel(startDate)} até ${formatDateLabel(endDate)}`
    }
    return periodLabel
  }

  const handleExportExcel = () => {
    if (reasons.length === 0) return

    const rows = reasons.map((reason, index) => ({
      Rank: index + 1,
      Motivo: reason.label || `Motivo ${reason.code}`,
      Quantidade: reason.count,
      Percentual: `${reason.percentage.toFixed(1)}%`,
    }))

    const ws = XLSX.utils.json_to_sheet(rows)
    const wb = XLSX.utils.book_new()
    XLSX.utils.book_append_sheet(wb, ws, 'MotivosNaoVenda')
    const timestamp = getFileTimestamp()
    XLSX.writeFile(wb, `seller-queue-motivos-nao-venda-ps-${pluginspaceId}-${timestamp}.xlsx`)
  }

  const handleExportPdf = () => {
    if (reasons.length === 0) return

    try {
      const doc = new jsPDF({ orientation: 'landscape' })
      const jsPDFAutoTable = require('jspdf-autotable')

      doc.setFontSize(14)
      doc.text('Motivos de Nao Venda', 14, 15)
      doc.setFontSize(10)
      doc.text(`Periodo: ${getPeriodText()}`, 14, 22)

      jsPDFAutoTable.autoTable(doc, {
        startY: 28,
        head: [['Rank', 'Motivo', 'Quantidade', 'Percentual']],
        body: reasons.map((reason, index) => [
          String(index + 1),
          reason.label || `Motivo ${reason.code}`,
          String(reason.count),
          `${reason.percentage.toFixed(1)}%`,
        ]),
        theme: 'grid',
        headStyles: { fillColor: [13, 110, 253], textColor: 255 },
      })

      const timestamp = getFileTimestamp()
      doc.save(`seller-queue-motivos-nao-venda-ps-${pluginspaceId}-${timestamp}.pdf`)
    } catch (error) {
      console.error('Erro ao exportar PDF de motivos de nao venda:', error)
    }
  }

  if (loading) {
    return (
      <div className='d-flex justify-content-center align-items-center' style={{ minHeight: '300px' }}>
        <div className='spinner-border' role='status'>
          <span className='sr-only'>Carregando...</span>
        </div>
      </div>
    )
  }

  return (
    <div>
      <div className='card mb-5'>
        <div className='card-header'>
          <h3 className='card-title'>Período</h3>
        </div>
        <div className='card-body'>
          <div className='d-flex flex-column gap-4'>
            <div className='btn-group' role='group'>
              <button
                type='button'
                className={`btn btn-outline-primary ${days === 7 ? 'active' : ''}`}
                onClick={() => handleFilterDays(7)}
              >
                Últimos 7 dias
              </button>
              <button
                type='button'
                className={`btn btn-outline-primary ${days === 30 ? 'active' : ''}`}
                onClick={() => handleFilterDays(30)}
              >
                Últimos 30 dias
              </button>
              <button
                type='button'
                className={`btn btn-outline-primary ${days === 90 ? 'active' : ''}`}
                onClick={() => handleFilterDays(90)}
              >
                Últimos 90 dias
              </button>
            </div>

            <div className='row g-3 align-items-end'>
              <div className='col-12 col-md-auto'>
                <label className='form-label fw-bold'>Data Inicial</label>
                <input
                  type='date'
                  className='form-control'
                  style={{ maxWidth: 180 }}
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                />
              </div>
              <div className='col-12 col-md-auto'>
                <label className='form-label fw-bold'>Data Final</label>
                <input
                  type='date'
                  className='form-control'
                  style={{ maxWidth: 180 }}
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                />
              </div>
              <div className='col-12 col-md-auto ms-md-1'>
                <label className='form-label d-block opacity-0 user-select-none'>Ações</label>
                <div className='d-flex align-items-center gap-2'>
                  <button
                    type='button'
                    className='btn btn-primary d-flex align-items-center justify-content-center'
                    style={{ width: 38, height: 38, padding: 0 }}
                    onClick={handleFilterByDateRange}
                    disabled={!startDate || !endDate}
                    title='Filtrar período'
                    aria-label='Filtrar período'
                  >
                    <i className='fas fa-search'></i>
                  </button>
                  <button
                    type='button'
                    className='btn btn-light d-flex align-items-center justify-content-center'
                    style={{ width: 38, height: 38, padding: 0 }}
                    onClick={handleClearDateRange}
                    title='Limpar período'
                    aria-label='Limpar período'
                  >
                    <i className='fas fa-eraser'></i>
                  </button>
                  <button
                    type='button'
                    className='btn btn-light-success d-flex align-items-center justify-content-center'
                    style={{ width: 38, height: 38, padding: 0 }}
                    onClick={handleExportExcel}
                    disabled={reasons.length === 0}
                    title='Exportar para Excel'
                    aria-label='Exportar para Excel'
                  >
                    <i className='fas fa-file-excel'></i>
                  </button>
                  <button
                    type='button'
                    className='btn btn-light-danger d-flex align-items-center justify-content-center'
                    style={{ width: 38, height: 38, padding: 0 }}
                    onClick={handleExportPdf}
                    disabled={reasons.length === 0}
                    title='Exportar para PDF'
                    aria-label='Exportar para PDF'
                  >
                    <i className='fas fa-file-pdf'></i>
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className='card'>
        <div className='card-header'>
          <h3 className='card-title'>{`Motivos de Não Venda (${periodLabel})`}</h3>
        </div>
        <div className='card-body'>
          {reasons.length === 0 ? (
            <div className='text-center py-10'>
              <p className='text-muted'>Nenhum motivo registrado neste período</p>
            </div>
          ) : (
            <div className='table-responsive'>
              <table className='table table-row-dashed table-row-gray-300 gy-7'>
                <thead>
                  <tr className='fw-bold fs-6 text-gray-800'>
                    <th style={{ width: '5%' }}>Rank</th>
                    <th>Motivo</th>
                    <th style={{ width: '15%' }} className='text-end'>
                      Quantidade
                    </th>
                    <th style={{ width: '25%' }} className='text-end'>
                      Percentual
                    </th>
                    <th style={{ width: '15%' }}>Barra</th>
                  </tr>
                </thead>
                <tbody>
                  {reasons.map((reason, index) => (
                    <tr key={reason.code}>
                      <td className='fw-bold text-primary'>{index + 1}</td>
                      <td>
                        <span className='fw-bold'>{reason.label || `Motivo ${reason.code}`}</span>
                      </td>
                      <td className='text-end'>{reason.count}</td>
                      <td className='text-end fw-bold'>{reason.percentage.toFixed(1)}%</td>
                      <td>
                        <div className='progress' style={{ height: '5px' }}>
                          <div
                            className='progress-bar'
                            role='progressbar'
                            style={{ width: `${reason.percentage}%` }}
                            aria-valuenow={reason.percentage}
                            aria-valuemin={0}
                            aria-valuemax={100}
                          ></div>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* Gráfico de Pizza simples (HTML + CSS) */}
      {reasons.length > 0 && (
        <div className='card mt-5'>
          <div className='card-header'>
            <h3 className='card-title'>{`Distribuição (${periodLabel})`}</h3>
          </div>
          <div className='card-body d-flex justify-content-center'>
            <div className='position-relative' style={{ width: '300px', height: '300px' }}>
              {(() => {
                let currentAngle = 0
                const gradientStops = reasons
                  .map((r, index) => {
                    const hue = (index * 360) / reasons.length
                    const startAngle = currentAngle
                    const sliceAngle = Math.max(0, Math.min(100, r.percentage)) * 3.6
                    currentAngle += sliceAngle
                    return `hsl(${hue}, 70%, 60%) ${startAngle}deg ${currentAngle}deg`
                  })
                  .join(', ')

                return (
              <div
                className='rounded-circle d-flex align-items-center justify-content-center fw-bold fs-3 text-white'
                style={{
                  width: '100%',
                  height: '100%',
                  background: `conic-gradient(${gradientStops})`,
                }}
              >
                <div className='text-center'>
                  <div className='fs-5 fw-bold'>Total</div>
                  <div className='fs-6'>{reasons.reduce((sum, r) => sum + r.count, 0)}</div>
                </div>
              </div>
                )
              })()}
            </div>
          </div>
        </div>
      )}

      {/* Legenda */}
      {reasons.length > 0 && (
        <div className='card mt-5'>
          <div className='card-header'>
            <h3 className='card-title'>Legenda</h3>
          </div>
          <div className='card-body'>
            <div className='row g-3'>
              {reasons.map((reason, index) => {
                const hue = (index * 360) / reasons.length
                return (
                  <div key={reason.code} className='col-md-6 col-lg-4'>
                    <div className='d-flex align-items-center'>
                      <div
                        className='rounded-circle me-2'
                        style={{
                          width: '20px',
                          height: '20px',
                          backgroundColor: `hsl(${hue}, 70%, 60%)`,
                        }}
                      ></div>
                      <div className='flex-grow-1'>
                        <div className='fw-bold'>{reason.label || `Motivo ${reason.code}`}</div>
                        <small className='text-muted'>{reason.count} ocorrências</small>
                      </div>
                    </div>
                  </div>
                )
              })}
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
